import { createContext, type ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  applyTheme,
  type ResolvedTheme,
  readPreference,
  resolveTheme,
  type ThemePreference,
  writePreference,
} from './theme.js';

interface ThemeContextValue {
  readonly preference: ThemePreference;
  readonly resolved: ResolvedTheme;
  setPreference(preference: ThemePreference): void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** Hält die Farbvariante (hell/dunkel/System), speichert sie und folgt dem System bei "system". */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(() => readPreference());
  const [systemDark, setSystemDark] = useState<boolean>(
    () => window.matchMedia(DARK_QUERY).matches,
  );

  useEffect(() => {
    const query = window.matchMedia(DARK_QUERY);
    const onChange = (event: MediaQueryListEvent) => setSystemDark(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    applyTheme(preference, systemDark);
  }, [preference, systemDark]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      preference,
      resolved: resolveTheme(preference, systemDark),
      setPreference: (next) => {
        writePreference(next);
        setPreferenceState(next);
      },
    }),
    [preference, systemDark],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/** Aufgelöster Farbmodus für Bausteine, die auch ohne Anbieter laufen müssen (Diagramme); Standard hell. */
export function useResolvedTheme(): ResolvedTheme {
  return useContext(ThemeContext)?.resolved ?? 'light';
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === null) {
    throw new Error('useTheme muss innerhalb von ThemeProvider verwendet werden');
  }
  return context;
}
